'use client';

import { useRouter } from 'next/navigation';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useAudio } from '../lib/AudioProvider';
import type { AudioEngine } from '../lib/AudioProvider';
import { useIsMobile } from '../lib/useIsMobile';


// ─── Types ────────────────────────────────────────────────────────────────────

interface ParticipantInfo {
  identity: string;
  name: string;
}

interface RoomInfo {
  name: string;
  numParticipants: number;
  creationTime: number;
  participants: ParticipantInfo[];
}

type TermLine =
  | { kind: 'boot';  text: string }
  | { kind: 'scan';  text: string }
  | { kind: 'empty'; text: string }
  | { kind: 'error'; text: string };

interface Transmission {
  sha:          string;
  date:         string;
  time:         string;
  transmission: string;
}

function groupByDate(entries: Transmission[]) {
  const groups: { date: string; items: Transmission[] }[] = [];
  const map = new Map<string, Transmission[]>();
  for (const t of entries) {
    if (!map.has(t.date)) {
      const items: Transmission[] = [];
      map.set(t.date, items);
      groups.push({ date: t.date, items });
    }
    map.get(t.date)!.push(t);
  }
  return groups;
}

function timeOfDay(time: string): { glyph: string; label: string } | null {
  if (!time) return null;
  const h = parseInt(time.split(':')[0], 10);
  if (h <= 4)  return { glyph: '◈', label: 'DEEP SIGNAL'  };
  if (h <= 9)  return { glyph: '○', label: 'SECTOR QUIET' };
  if (h <= 17) return { glyph: '▪', label: 'SECTOR IDLE'  };
  if (h <= 21) return { glyph: '◑', label: 'SIGNAL RISE'  };
  return           { glyph: '●', label: 'SIGNAL PEAK'  };
}

// Audio engine is provided by AudioProvider in layout.tsx — see lib/AudioProvider.tsx

// Wraps a line's text with the directory prefix for display
// Log/system messages use "root"; the user input prompt uses "aperture_name"
function prefixedLine(sectorId: string, text: string) {
  return `voidline/sector_${sectorId.toLowerCase()}/root> ${text}`;
}

// ─── Canvas FX (rain + glitch) — ported from voidline.gg ─────────────────────
// imgSrc is a dep so the effect re-runs once the <img> actually mounts.

function useCanvasFX(
  canvasRef: React.RefObject<HTMLCanvasElement>,
  imgRef:    React.RefObject<HTMLImageElement>,
  imgSrc:    string,
  navigatingRef: React.RefObject<boolean>,
) {
  useEffect(() => {
    if (!imgSrc) return;                        // img not yet in DOM
    const canvas = canvasRef.current;
    const img    = imgRef.current;
    if (!canvas || !img) return;

    const ctx = canvas.getContext('2d')!;

    const rand    = (a: number, b: number) => Math.random() * (b - a) + a;
    const randInt = (a: number, b: number) => Math.floor(rand(a, b));

    function resize() {
      canvas!.width  = window.innerWidth;
      canvas!.height = window.innerHeight;
    }
    window.addEventListener('resize', resize);
    resize();

    function imgRect() {
      const vw = window.innerWidth,  vh = window.innerHeight;
      const iw = img!.naturalWidth  || vw;
      const ih = img!.naturalHeight || vh;
      const sc = Math.min(vw / iw, vh / ih);
      const dw = iw * sc, dh = ih * sc;
      return { x: (vw - dw) / 2, y: (vh - dh) / 2, w: dw, h: dh };
    }

    // ── Rain ──
    interface Drop { x:number; y:number; len:number; spd:number; op:number; w:number; ang:number; }
    function mkDrop(seeded: boolean): Drop {
      const vw = canvas!.width, vh = canvas!.height;
      return {
        x:   rand(0, vw),
        y:   seeded ? rand(0, vh) : rand(-110, -5),
        len: rand(18, 65),
        spd: rand(7, 22),
        op:  rand(0.2, 0.6),
        w:   rand(0.6, 1.4),
        ang: rand(-0.05, 0.03),
      };
    }
    const drops: Drop[] = [];
    for (let i = 0; i < 220; i++) drops.push(mkDrop(true));

    function drawRain() {
      const vw = canvas!.width, vh = canvas!.height;
      for (const d of drops) {
        const dx = Math.sin(d.ang) * d.len;
        const dy = Math.cos(d.ang) * d.len;
        const g  = ctx.createLinearGradient(d.x, d.y, d.x + dx, d.y + dy);
        g.addColorStop(0,    'rgba(210,120,30,0)');
        g.addColorStop(0.45, `rgba(225,138,45,${d.op})`);
        g.addColorStop(0.55, `rgba(235,148,55,${d.op})`);
        g.addColorStop(1,    'rgba(190,100,20,0)');
        ctx.beginPath();
        ctx.moveTo(d.x, d.y);
        ctx.lineTo(d.x + dx, d.y + dy);
        ctx.strokeStyle = g; ctx.lineWidth = d.w; ctx.stroke();
        d.x += Math.sin(d.ang) * d.spd * 0.3;
        d.y += Math.cos(d.ang) * d.spd;
        if (d.y - d.len > vh || d.x > vw + 50 || d.x < -50)
          Object.assign(d, mkDrop(false));
      }
    }

    // ── Glitch engine ──
    let gTimer = 0, gType = 'none', gElapsed = 0, gDur = 0, gTotalDur = 0;
    let rgbStartOff = 0, rgbCurOff = 0;
    interface Slice     { y:number; h:number; dx:number; }
    interface ScanBlock { x:number; y:number; w:number; h:number; }
    let slices: Slice[] = [], scanBlocks: ScanBlock[] = [];

    function buildSlices() {
      const r = imgRect(); slices = [];
      const n = randInt(2, 4);
      for (let i = 0; i < n; i++)
        slices.push({
          y:  r.y + rand(0, r.h),
          h:  rand(2, r.h * 0.06),
          dx: (Math.random() < 0.5 ? 1 : -1) * rand(4, r.w * 0.07),
        });
    }

    function startRGB(startOff: number, dur: number) {
      gType = 'rgb'; rgbStartOff = startOff; rgbCurOff = startOff;
      gDur  = dur;   gTotalDur   = dur;      gElapsed  = 0;
    }

    function buildScanBlocks() {
      const r = imgRect(); scanBlocks = [];
      const n = randInt(1, 4);
      for (let i = 0; i < n; i++) {
        const bh = rand(r.h * 0.04, r.h * 0.11);
        const bw = rand(r.w * 0.20, r.w * 0.55);
        scanBlocks.push({ x: r.x + rand(0, r.w - bw), y: r.y + rand(0, r.h - bh), w: bw, h: bh });
      }
    }

    function triggerGlitch() {
      const roll = Math.random();
      if      (roll < 0.38) { gType='slice'; gDur=rand(1.5,2.8); gTotalDur=gDur; gElapsed=0; buildSlices(); }
      else if (roll < 0.75) { gType='scan';  gDur=1.0;           gTotalDur=1.0;  gElapsed=0; buildScanBlocks(); }
      else                  { startRGB(rand(12, 22), rand(2.0, 3.2)); }
    }

    function drawGlitch() {
      if (!img!.naturalWidth) return;
      const r = imgRect();
      if (gType === 'rgb') {
        if (rgbCurOff < 0.4) return;
        ctx.save();
        ctx.globalCompositeOperation = 'screen'; ctx.globalAlpha = 0.22;
        ctx.filter = 'saturate(10) hue-rotate(0deg) brightness(1.4)';
        ctx.drawImage(img!, r.x - rgbCurOff, r.y, r.w, r.h);
        ctx.filter = 'saturate(10) hue-rotate(200deg) brightness(1.4)';
        ctx.drawImage(img!, r.x + rgbCurOff, r.y, r.w, r.h);
        ctx.filter = 'none'; ctx.restore();
      } else if (gType === 'slice') {
        ctx.save();
        for (const s of slices) {
          ctx.save();
          ctx.beginPath(); ctx.rect(r.x, s.y, r.w, s.h); ctx.clip();
          ctx.drawImage(img!, r.x + s.dx, r.y, r.w, r.h);
          ctx.restore();
        }
        ctx.restore();
      } else if (gType === 'scan') {
        const flicker = Math.random() < 0.5;
        ctx.save(); ctx.globalAlpha = flicker ? 0.82 : 0.55;
        for (const b of scanBlocks) {
          ctx.fillStyle = 'rgba(0,0,0,1)';
          ctx.fillRect(b.x, b.y, b.w, b.h);
          ctx.fillStyle = 'rgba(78,232,212,0.28)';
          for (let sy = b.y; sy < b.y + b.h; sy += 3)
            ctx.fillRect(b.x, sy, b.w, 1);
        }
        ctx.restore();
      }
    }

    // ── Boot + loop ──
    let booting = true;
    const BOOT_DUR = 2.6, BOOT_OFF = 24;
    gType = 'rgb'; rgbCurOff = BOOT_OFF; gElapsed = 0; gTotalDur = BOOT_DUR;

    let last = 0, animId: number | null = null;
    let timeScale = 1.0, targetScale = 1.0;

    function loop(ts: number) {
      animId = requestAnimationFrame(loop);
      const rawDt = Math.min((ts - last) / 1000, 0.05); last = ts;
      timeScale += (targetScale - timeScale) * 0.015;
      const dt = rawDt * timeScale;
      if (targetScale === 0 && Math.random() > timeScale) {
        if (timeScale < 0.005) { cancelAnimationFrame(animId!); animId = null; }
        return;
      }
      ctx.clearRect(0, 0, canvas!.width, canvas!.height);
      drawRain();
      if (booting) {
        gElapsed += dt;
        const t = Math.min(gElapsed / BOOT_DUR, 1);
        rgbCurOff = BOOT_OFF * (1 - t * t);
        drawGlitch();
        if (gElapsed >= BOOT_DUR) { booting = false; gType = 'none'; gTimer = rand(6, 12); }
      } else if (navigatingRef.current) {
        // ── Chaos mode: all glitch types fire simultaneously while page loads ──
        // Slices — rebuild aggressively every few frames
        buildSlices();
        ctx.save();
        for (const s of slices) {
          ctx.save();
          ctx.beginPath(); ctx.rect(imgRect().x, s.y, imgRect().w, s.h); ctx.clip();
          ctx.drawImage(img!, imgRect().x + s.dx * rand(1.5, 3), imgRect().y, imgRect().w, imgRect().h);
          ctx.restore();
        }
        ctx.restore();
        // Scan blocks — big, flickery
        buildScanBlocks();
        const flicker = Math.random() < 0.6;
        ctx.save(); ctx.globalAlpha = flicker ? 0.9 : 0.6;
        for (const b of scanBlocks) {
          ctx.fillStyle = 'rgba(0,0,0,1)';
          ctx.fillRect(b.x, b.y, b.w, b.h);
          ctx.fillStyle = 'rgba(78,232,212,0.35)';
          for (let sy = b.y; sy < b.y + b.h; sy += 3)
            ctx.fillRect(b.x, sy, b.w, 1);
        }
        ctx.restore();
        // RGB split — strong constant offset
        rgbCurOff = rand(6, 18);
        ctx.save();
        ctx.globalCompositeOperation = 'screen'; ctx.globalAlpha = 0.28;
        ctx.filter = 'saturate(10) hue-rotate(0deg) brightness(1.4)';
        ctx.drawImage(img!, imgRect().x - rgbCurOff, imgRect().y, imgRect().w, imgRect().h);
        ctx.filter = 'saturate(10) hue-rotate(200deg) brightness(1.4)';
        ctx.drawImage(img!, imgRect().x + rgbCurOff, imgRect().y, imgRect().w, imgRect().h);
        ctx.filter = 'none'; ctx.restore();
      } else {
        if (gType === 'none') {
          gTimer -= dt;
          if (gTimer <= 0) triggerGlitch();
        } else {
          gElapsed += dt; gDur -= dt;
          if (gType === 'rgb') {
            const t = Math.min(gElapsed / gTotalDur, 1);
            rgbCurOff = rgbStartOff * (1 - t * t);
          }
          drawGlitch();
          if (gType === 'slice' && Math.random() < 0.08) buildSlices();
          if (gDur <= 0) { gType = 'none'; gTimer = rand(6, 12); }
        }
      }
    }

    function slowDown() { targetScale = 0; }
    function speedUp() {
      targetScale = 1;
      if (!animId) { last = performance.now(); animId = requestAnimationFrame(loop); }
    }

    function start() { if (!animId) { last = performance.now(); animId = requestAnimationFrame(loop); } }
    if (img.complete && img.naturalWidth) start();
    else img.addEventListener('load', start);

    // Randomise CSS glitch animation offsets
    ['#vl-title-name', '#vl-coords', '#vl-pressure'].forEach(sel => {
      const el = document.querySelector(sel) as HTMLElement | null;
      if (el) el.style.animationDelay = `-${(Math.random() * 7).toFixed(2)}s`;
    });

    // Focus tracking: Electron IPC path (main-process blur/focus, authoritative for
    // OS-level alt-tab) or web fallback (visibilitychange + window blur/focus).
    let cleanupFocus: (() => void) | null = null;
    if (window.electronAPI?.onWindowBlur) {
      const offBlur  = window.electronAPI.onWindowBlur(slowDown);
      const offFocus = window.electronAPI.onWindowFocus(speedUp);
      cleanupFocus = () => { offBlur(); offFocus(); };
    } else {
      const onVis = () => { document.hidden ? slowDown() : speedUp(); };
      document.addEventListener('visibilitychange', onVis);
      window.addEventListener('blur',  slowDown);
      window.addEventListener('focus', speedUp);
      cleanupFocus = () => {
        document.removeEventListener('visibilitychange', onVis);
        window.removeEventListener('blur',  slowDown);
        window.removeEventListener('focus', speedUp);
      };
    }

    return () => {
      window.removeEventListener('resize', resize);
      if (animId !== null) cancelAnimationFrame(animId);
      cleanupFocus?.();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imgSrc]);   // re-run when src is set — canvasRef/imgRef are stable refs
}

// ─── Classification bars ──────────────────────────────────────────────────────

function useClassBars(containerRef: React.RefObject<HTMLDivElement>) {
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const rand    = (a: number, b: number) => Math.random() * (b - a) + a;
    const randInt = (a: number, b: number) => Math.floor(rand(a, b));
    const barEls  = Array.from(container.querySelectorAll('span')) as HTMLSpanElement[];
    const BAR_COUNT = barEls.length;
    let prevDippers: number[] = [];
    let timerId: ReturnType<typeof setTimeout>;

    function dipBars() {
      prevDippers.forEach(i => {
        barEls[i].style.height     = '100%';
        barEls[i].style.background = 'var(--vl-cyan)';
        barEls[i].style.boxShadow  = '0 0 4px rgba(78,232,212,0.3)';
      });
      const indices = [...Array(BAR_COUNT).keys()];
      for (let i = indices.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [indices[i], indices[j]] = [indices[j], indices[i]];
      }
      const dippers = indices.slice(0, randInt(1, 4));
      prevDippers   = dippers;
      dippers.forEach(i => {
        barEls[i].style.height     = `${rand(5, 75)}%`;
        barEls[i].style.background = 'var(--vl-red-b)';
        barEls[i].style.boxShadow  = '0 0 6px rgba(232,78,58,0.5)';
      });
      timerId = setTimeout(dipBars, rand(3000, 5000));
    }

    timerId = setTimeout(dipBars, rand(3000, 5000));
    return () => clearTimeout(timerId);
  }, [containerRef]);
}

// ─── Clock ────────────────────────────────────────────────────────────────────

function useClock() {
  const [time, setTime] = useState('--:--:--');
  useEffect(() => {
    function tick() {
      const n = new Date();
      setTime(
        String(n.getHours()).padStart(2, '0')   + ':' +
        String(n.getMinutes()).padStart(2, '0') + ':' +
        String(n.getSeconds()).padStart(2, '0'),
      );
    }
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);
  return time;
}

// ─── Anomaly radius ticker ────────────────────────────────────────────────────

function useRadius() {
  const [radius, setRadius] = useState('— m');
  useEffect(() => {
    let rv = 312, rd = 1;
    const id = setInterval(() => {
      if (Math.random() < 0.04) rd *= -1;
      rv += rd * Math.floor(Math.random() * 3);
      rv = Math.max(200, Math.min(600, rv));
      setRadius(`${rv} m`);
    }, 900);
    return () => clearInterval(id);
  }, []);
  return radius;
}


// ─── Room name validation ─────────────────────────────────────────────────────

function validateRoomName(name: string): string | null {
  const t = name.trim();
  if (!t)                            return 'DESIGNATION REQUIRED';
  if (/\s/.test(t))                  return 'NO SPACES PERMITTED';
  if (t.length > 64)                 return 'DESIGNATION TOO LONG';
  if (!/^[a-zA-Z0-9_\-]+$/.test(t)) return 'ALPHANUMERIC / HYPHENS / UNDERSCORES ONLY';
  return null;
}

// ─── Terminal console ─────────────────────────────────────────────────────────

const BOOT_LINES = [
  'VOIDLINE UPLINK // LOCAL NODE INIT',
  'SIGNAL LAYER: CONNECTED',
  'AUTHENTICATING SECTOR KEY...',
  'SECTOR KEY ACCEPTED',
];
// /ping is shown as a visible "typed" command before the query fires
const BOOT_PING_LINE  = '/ping';
const BOOT_QUERY_LINE = 'QUERYING ACTIVE APERTURES...';

const PING_COOLDOWN_MS = 3_000; // 3 seconds between manual pings

// Rooms that are always listed regardless of API state
const PINNED_ROOMS: RoomInfo[] = [
  { name: 'the-arc-neon', numParticipants: 0, creationTime: 0, participants: [] },
];

interface SectorStats {
  openApertures: number;
  activeSignals: number;
}

function TerminalConsole({ sectorId, onStats, onRooms, pingRef, navigatingRef }: { sectorId: string; onStats: (s: SectorStats) => void; onRooms: (r: RoomInfo[]) => void; pingRef: React.MutableRefObject<(() => void) | null>; navigatingRef: React.MutableRefObject<boolean> }) {
  const router = useRouter();
  const isMobile = useIsMobile(705);
  const [lines,      setLines]      = useState<TermLine[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [joining,    setJoining]    = useState<string | null>(null);
  const [booted,     setBooted]     = useState(false);
  const [focused,    setFocused]    = useState(false);
  const pingAvailableAt = useRef<number>(0);
  const outputRef = useRef<HTMLDivElement>(null);
  const inputRef  = useRef<HTMLInputElement>(null);

  // Auto-focus on mount (desktop only — avoids keyboard pop-up on mobile)
  useEffect(() => { if (!isMobile) { inputRef.current?.focus(); setFocused(true); } }, [isMobile]);

  // Scroll output to bottom on new lines
  useEffect(() => {
    const el = outputRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [lines]);

  // Boot sequence
  useEffect(() => {
    let cancelled = false;
    async function boot() {
      for (let i = 0; i < BOOT_LINES.length; i++) {
        await new Promise(r => setTimeout(r, i === 0 ? 300 : 260 + Math.random() * 200));
        if (cancelled) return;
        setLines(prev => [...prev, { kind: 'boot', text: prefixedLine(sectorId, BOOT_LINES[i]) }]);
      }
      // Show /ping as a "typed" command, then the query line
      await new Promise(r => setTimeout(r, 260 + Math.random() * 200));
      if (cancelled) return;
      setLines(prev => [...prev, { kind: 'scan', text: prefixedLine(sectorId, BOOT_PING_LINE) }]);
      await new Promise(r => setTimeout(r, 120));
      if (cancelled) return;
      setLines(prev => [...prev, { kind: 'boot', text: prefixedLine(sectorId, BOOT_QUERY_LINE) }]);
      if (!cancelled) setBooted(true);
    }
    boot();
    return () => { cancelled = true; };
  }, [sectorId]);

  const fetchRooms = useCallback(async (silent = false) => {
    try {
      const res = await fetch('/api/rooms');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const rooms: RoomInfo[] = (data.rooms ?? []).map((r: RoomInfo & { participants?: ParticipantInfo[] }) => ({
        ...r,
        participants: r.participants ?? [],
      }));
      const total = rooms.reduce((s: number, r: RoomInfo) => s + r.numParticipants, 0);
      onStats({ openApertures: rooms.length, activeSignals: total });
      // Merge pinned rooms (skip any that already appear in live results)
      const liveNames = new Set(rooms.map(r => r.name));
      const pinned = PINNED_ROOMS.filter(r => !liveNames.has(r.name));
      const allRooms = [...rooms, ...pinned];
      onRooms(allRooms);
      if (!silent) {
        if (allRooms.length === 0) {
          setLines(prev => [...prev,
            { kind: 'empty', text: prefixedLine(sectorId, '// NO APERTURES ACTIVE') },
            { kind: 'empty', text: prefixedLine(sectorId, 'SECTOR SIGNAL NOMINAL — 0 INHABITANTS UPLINK') },
          ]);
        } else {
          setLines(prev => [
            ...prev,
            { kind: 'scan', text: prefixedLine(sectorId, `${rooms.length} APERTURE${rooms.length !== 1 ? 'S' : ''} ACTIVE — ${total} SIGNAL${total !== 1 ? 'S' : ''}`) },
          ]);
        }
      }
    } catch {
      // On error, still show pinned rooms
      onStats({ openApertures: 0, activeSignals: 0 });
      onRooms(PINNED_ROOMS);
      if (!silent) {
        setLines(prev => [
          ...prev,
          { kind: 'error', text: prefixedLine(sectorId, 'ERR: SECTOR SIGNAL LOST') },
        ]);
      }
    }
  }, [sectorId, onStats, onRooms]);

  useEffect(() => {
    if (!booted) return;
    fetchRooms();  // boot fetch — shows result in console
    // Silent auto-poll every 15s — updates stats + aperture panel without console output.
    // Manual /ping and the ⟳ button remain for explicit user-triggered rescans.
    const id = setInterval(() => { fetchRooms(true); }, 15_000);
    return () => clearInterval(id);
  }, [booted, fetchRooms]);

  // Ping logic — shared between typed /ping and external trigger (aperture panel button)
  const doPing = useCallback(() => {
    const now = Date.now();
    if (now < pingAvailableAt.current) {
      const secsLeft = Math.ceil((pingAvailableAt.current - now) / 1000);
      setLines(prev => [...prev, { kind: 'error', text: prefixedLine(sectorId, `ERR: PING COOLDOWN — ${secsLeft}s REMAINING`) }]);
      return;
    }
    pingAvailableAt.current = now + PING_COOLDOWN_MS;
    setLines(prev => [...prev,
      { kind: 'scan', text: prefixedLine(sectorId, `/ping`) },
      { kind: 'scan', text: prefixedLine(sectorId, `SCANNING SECTOR ${sectorId}...`) },
    ]);
    fetchRooms();
  }, [sectorId, fetchRooms]);

  // Expose ping to parent via ref
  useEffect(() => {
    pingRef.current = doPing;
    return () => { pingRef.current = null; };
  }, [doPing, pingRef]);

  // Submit
  function handleSubmit() {
    const raw = inputValue.trim();

    // /ping command — manual rescan with cooldown
    if (raw === '/ping') {
      setInputValue('');
      doPing();
      return;
    }

    const err = validateRoomName(raw);
    if (err) {
      setLines(prev => [...prev, { kind: 'error', text: prefixedLine(sectorId, `ERR: ${err}`) }]);
      return;
    }
    const name = raw;
    setLines(prev => [...prev, { kind: 'scan', text: prefixedLine(sectorId, `> OPENING APERTURE: ${name.toUpperCase()}`) }]);
    setJoining(name);
    navigatingRef.current = true;
    router.push(`/rooms/${encodeURIComponent(name)}`);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') handleSubmit();
  }

  function handleTermClick() {
    inputRef.current?.focus();
  }

  return (
    <div id="vl-terminal" className={focused ? 'vl-term-focused' : ''} onClick={handleTermClick}>
      {/* Header bar */}
      <div className="vl-term-header">
        <span className="vl-term-header-id">LOCAL SYSTEM NODE // SECTOR {sectorId}</span>
        <span className="vl-term-header-sig">● SIGNAL LOCK</span>
      </div>

      {/* Output area */}
      <div className="vl-term-output" ref={outputRef}>
        {lines.map((line, i) => {
          const cls =
            line.kind === 'error' ? 'vl-term-line vl-term-line-err'  :
            line.kind === 'empty' ? 'vl-term-line vl-term-line-dim'  :
            line.kind === 'boot'  ? 'vl-term-line vl-term-line-boot' :
            'vl-term-line';
          return <div key={i} className={cls}>{line.text}</div>;
        })}
      </div>

      {/* Prompt / input line */}
      <div className="vl-term-prompt-line">
        <span className="vl-term-prompt-path">voidline/sector_{sectorId.toLowerCase()}/aperture_name</span>
        <span className="vl-term-prompt-char">&gt;</span>
        <div className="vl-term-input-wrap" onClick={handleTermClick}>
          <span className="vl-term-input-sizer" aria-hidden="true">{inputValue}</span>
          <input
            ref={inputRef}
            className="vl-term-input"
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => setFocused(true)}
            onBlur={() => { if (!isMobile) setTimeout(() => inputRef.current?.focus(), 0); else setFocused(false); }}
            disabled={joining !== null}
            autoComplete="off"
            spellCheck={false}
            aria-label="Aperture designation"
          />
          <span className="vl-term-cursor" />
        </div>
      </div>
    </div>
  );
}

// ─── Boot overlay ─────────────────────────────────────────────────────────────

const BOOT_OVERLAY_LINES = [
  { text: 'LOCAL SYSTEM NODE // COLD START',            delay: 0   },
  { text: 'SECTOR SOVEREIGNTY LAYER: ONLINE',           delay: 100 },
  { text: 'SIGNAL LAYER: INITIALIZING...',              delay: 190 },
  { text: 'SCANNING APERTURE REGISTRY...',              delay: 290 },
  { text: 'LOADING UPLINK PROTOCOL v2.17',              delay: 390 },
  { text: 'CUSTODIAN HANDSHAKE: PENDING...',            delay: 500 },
  { text: 'DRIFT COMPENSATION: ENGAGED',                delay: 600 },
  { text: 'SECTOR R-77 // SIGNAL LOCK CONFIRMED',       delay: 700 },
  { text: 'MATERIALIZING LOCAL NODE INTERFACE...',      delay: 810 },
];

function BootOverlay() {
  const [lines,   setLines]   = useState<string[]>([]);
  const [exiting, setExiting] = useState(false);
  const [gone,    setGone]    = useState(false);

  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];

    BOOT_OVERLAY_LINES.forEach(({ text, delay }) => {
      timers.push(setTimeout(() => setLines(prev => [...prev, text]), delay));
    });

    // Start fade-out much sooner so page animations aren't masked
    timers.push(setTimeout(() => setExiting(true),  900));
    timers.push(setTimeout(() => setGone(true),     1500));

    return () => timers.forEach(clearTimeout);
  }, []);

  if (gone) return null;

  return (
    <div id="vl-boot-overlay" className={exiting ? 'vl-boot-exit' : ''}>
      <div id="vl-boot-terminal">
        <div id="vl-boot-header">
          <span>VOIDLINE SYSTEMS // SECTOR R-77</span>
          <span id="vl-boot-blink">● SIGNAL</span>
        </div>
        <div id="vl-boot-lines">
          {lines.map((l, i) => (
            <div key={i} className="vl-boot-line">
              <span className="vl-boot-prompt">&gt;&nbsp;</span>{l}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}



// ─── Special room config ─────────────────────────────────────────────────────

const SPECIAL_ROOMS: Record<string, { icon: string; className: string; label: [string, string]; sortOrder: number }> = {
  'the-arc-neon': { icon: '✦', className: 'vl-ap-room-arc', label: ['GUEST', 'GUESTS'], sortOrder: -1 },
};
const DEFAULT_ROOM_LABEL: [string, string] = ['INHABITANT', 'INHABITANTS'];

// ─── Aperture Panel (top-right corner manifest) ───────────────────────────────

function AperturePanel({ sectorId, rooms, visible, onPing, navigatingRef }: { sectorId: string; rooms: RoomInfo[]; visible: boolean; onPing: () => void; navigatingRef: React.MutableRefObject<boolean> }) {
  const router = useRouter();
  const [joining, setJoining] = useState<string | null>(null);
  // Per-room independent glitch offsets, seeded on mount
  const glitchOffsets = useRef<number[]>([]);
  useEffect(() => {
    glitchOffsets.current = rooms.map(() => parseFloat((Math.random() * 9).toFixed(2)));
  }, [rooms.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sort rooms: special rooms with lower sortOrder first, then alphabetical
  const sortedRooms = [...rooms].sort((a, b) => {
    const sa = SPECIAL_ROOMS[a.name]?.sortOrder ?? 0;
    const sb = SPECIAL_ROOMS[b.name]?.sortOrder ?? 0;
    if (sa !== sb) return sa - sb;
    return a.name.localeCompare(b.name);
  });

  if (!visible) return null;

  return (
    <div id="vl-aperture-panel">
      <div className="vl-ap-header">
          <button className="vl-ap-refresh" onClick={(e) => { e.stopPropagation(); onPing(); }} title="Rescan apertures" aria-label="Rescan apertures">⟳</button>
          <span className="vl-ap-title">APERTURE MANIFEST</span>
          <span className="vl-ap-count">{sortedRooms.length}</span>
        </div>
        <div className="vl-ap-list">
          {sortedRooms.length === 0 ? (
            <div className="vl-ap-empty">{'// NO APERTURES ACTIVE'}</div>
          ) : (
            sortedRooms.map((r, idx) => {
              const special = SPECIAL_ROOMS[r.name];
              const labelPair = special?.label ?? DEFAULT_ROOM_LABEL;
              const label = r.numParticipants === 1 ? labelPair[0] : labelPair[1];
              const glitchDelay = `-${(glitchOffsets.current[idx] ?? 0).toFixed(2)}s`;
              return (
                <button
                  key={r.name}
                  className={`vl-ap-room${special ? ` ${special.className}` : ''}`}
                  disabled={joining !== null}
                  onClick={() => {
                    if (!joining) {
                      setJoining(r.name);
                      navigatingRef.current = true;
                      router.push(`/rooms/${encodeURIComponent(r.name)}`);
                    }
                  }}
                >
                  {/* Room name row */}
                  <div className="vl-ap-room-header">
                    <span className="vl-ap-indicator">{special?.icon ?? '▶'}</span>
                    <span
                      className="vl-ap-name vl-ap-glitch"
                      style={{ animationDelay: glitchDelay }}
                    >
                      {r.name}
                    </span>
                    <span className="vl-ap-inh-count">
                      {r.numParticipants}
                      <span className="vl-ap-inh-label"> {label}</span>
                    </span>
                  </div>
                  {/* Participant names */}
                  {r.participants.length > 0 && (
                    <div className="vl-ap-participants">
                      {r.participants.map((p) => (
                        <div key={p.identity} className="vl-ap-participant">
                          <span className="vl-ap-participant-dot">●</span>
                          {(p.name || p.identity).toUpperCase()}
                        </div>
                      ))}
                    </div>
                  )}
                </button>
              );
            })
          )}
        </div>
        <div className="vl-ap-footer">
          SECTOR {sectorId} {'// UPLINK NODE'}
        </div>
    </div>
  );
}

// ─── Signal Panel (top-left, audio control) ───────────────────────────────────

/** Shared signal panel content — used by both desktop panel and mobile drawer */
function SignalPanelContent({ audio, collapsed, toggleCollapse, closeLabel }: {
  audio: AudioEngine; collapsed: boolean; toggleCollapse: () => void; closeLabel?: string;
}) {
  return (
    <>
      {/* Header: aux path + collapse toggle */}
      <div className="vl-sp-header">
        <span className="vl-sp-title">aux://sector_r-77/signal/</span>
        <button
          className="vl-sp-collapse-btn"
          onClick={(e) => { e.stopPropagation(); toggleCollapse(); }}
          aria-label={collapsed ? 'Expand signal panel' : 'Collapse signal panel'}
        >{closeLabel ?? (collapsed ? '▶' : '◀')}</button>
      </div>

      {!collapsed && (
        <>
          {/* Transport + volume — single compact row */}
          <div className="vl-sp-controls">
            <div className="vl-sp-transport">
              <button
                className="vl-sp-btn vl-sp-prev"
                onClick={(e) => { e.stopPropagation(); audio.prev(); }}
                aria-label="Previous track"
              >{'<<'}</button>
              <button
                className={`vl-sp-btn vl-sp-playpause${audio.playing ? ' vl-sp-is-playing' : ''}`}
                onClick={(e) => { e.stopPropagation(); audio.toggle(); }}
                aria-label={audio.playing ? 'Pause' : 'Play'}
              >
                {audio.playing
                  ? <span className="vl-sp-pause-icon" aria-hidden="true"><span/><span/></span>
                  : '▶'}
              </button>
              <button
                className="vl-sp-btn vl-sp-next"
                onClick={(e) => { e.stopPropagation(); audio.next(); }}
                aria-label="Next track"
              >{'>>'}</button>
            </div>
            <div className="vl-sp-vol-row">
              <span className="vl-sp-vol-label">VOL</span>
              <input
                className="vl-sp-vol"
                type="range"
                min={0}
                max={1}
                step={0.01}
                value={audio.volume}
                onChange={(e) => audio.setVolume(parseFloat(e.target.value))}
                aria-label="Volume"
              />
            </div>
          </div>

          {/* Filename */}
          <div className="vl-sp-track">
            <span className="vl-sp-filename">{audio.trackTitle}</span>
          </div>

          {/* Footer: attribution only */}
          <div className="vl-sp-footer">
            <span className="vl-sp-credit">KARL CASEY @ WHITE BAT AUDIO</span>
          </div>
        </>
      )}
    </>
  );
}

function SignalPanel({ audio, visible }: { audio: AudioEngine; visible: boolean }) {
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem('vl_sp_collapsed') === '1';
  });

  function toggleCollapse() {
    setCollapsed(prev => {
      const next = !prev;
      localStorage.setItem('vl_sp_collapsed', next ? '1' : '0');
      return next;
    });
  }

  if (!visible) return null;

  return (
    <div id="vl-signal-panel" className={collapsed ? 'vl-sp-collapsed' : ''}>
      <SignalPanelContent audio={audio} collapsed={collapsed} toggleCollapse={toggleCollapse} />
    </div>
  );
}

/** Mobile-only: floating SIG button + bottom-sheet drawer for signal panel */
function SignalDrawer({ audio, visible }: { audio: AudioEngine; visible: boolean }) {
  const [open, setOpen] = useState(false);
  const drawerRef = useRef<HTMLDivElement>(null);
  const touchRef = useRef<{ startY: number; startTime: number } | null>(null);

  // Swipe-up from bottom 60px of screen to open
  useEffect(() => {
    function onTouchStart(e: TouchEvent) {
      const y = e.touches[0].clientY;
      if (y > window.innerHeight - 60) {
        touchRef.current = { startY: y, startTime: Date.now() };
      }
    }
    function onTouchEnd(e: TouchEvent) {
      if (!touchRef.current) return;
      const dy = touchRef.current.startY - e.changedTouches[0].clientY;
      const dt = Date.now() - touchRef.current.startTime;
      touchRef.current = null;
      // Swipe up at least 50px within 400ms
      if (dy > 50 && dt < 400) setOpen(true);
    }
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchend', onTouchEnd, { passive: true });
    return () => {
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchend', onTouchEnd);
    };
  }, []);

  // Close on backdrop tap
  function onBackdropClick(e: React.MouseEvent) {
    if (e.target === e.currentTarget) setOpen(false);
  }

  if (!visible) return null;

  return (
    <>
      {/* Floating music button */}
      <button
        className={`vl-sig-fab${audio.playing ? ' vl-sig-fab-playing' : ''}`}
        onClick={() => setOpen(true)}
        aria-label="Open signal panel"
      >
        {audio.playing
          ? <span className="vl-sig-fab-eq"><span/><span/><span/></span>
          : <span className="vl-sig-fab-note">{'\u266B'}</span>}
      </button>

      {/* Drawer backdrop + panel */}
      {open && (
        <div className="vl-sig-backdrop" onClick={onBackdropClick}>
          <div className="vl-sig-drawer" ref={drawerRef}>
            <div className="vl-sig-drawer-handle" />
            <SignalPanelContent audio={audio} collapsed={false} toggleCollapse={() => setOpen(false)} closeLabel="✕" />
          </div>
        </div>
      )}
    </>
  );
}

// ─── Transmission Log ────────────────────────────────────────────────────────

function TransmissionLog({ variant = 'panel' }: { variant?: 'panel' | 'mobile' }) {
  const [open,    setOpen]    = useState(false);
  const [entries, setEntries] = useState<Transmission[] | null>(null);
  const [loading, setLoading] = useState(false);
  const cacheRef = useRef<Transmission[] | null>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open]);

  async function openLog() {
    setOpen(true);
    if (cacheRef.current) { setEntries(cacheRef.current); return; }
    setLoading(true);
    try {
      const res  = await fetch('/transmissions.json');
      const data = await res.json() as Transmission[];
      cacheRef.current = data;
      setEntries(data);
    } catch { setEntries([]); }
    finally  { setLoading(false); }
  }

  return (
    <>
      {variant === 'panel' ? (
        /* Compact panel trigger — sits in the left rail below the Signal Panel */
        <button className="vl-sr-trigger" onClick={openLog} aria-label="Open transmission log">
          <span className="vl-sr-path">rec://</span>
          <span className="vl-sr-label">{'// SECTOR RECORD'}</span>
          {entries !== null && <span className="vl-sr-count">{entries.length}</span>}
          <span className="vl-sr-arrow">▸</span>
        </button>
      ) : (
        /* Mobile text trigger — rendered below terminal, shown only on mobile */
        <button className="vl-sr-mobile-btn" onClick={openLog} aria-label="Open transmission log">
          {'// SECTOR RECORD'}
        </button>
      )}

      {open && (
        <div
          className="vl-txlog-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
        >
          <div className="vl-txlog-panel">
            {/* Header */}
            <div className="vl-txlog-hdr">
              <span className="vl-txlog-hdr-path">tx://sector_r-77/record/</span>
              <span className="vl-txlog-hdr-title">TRANSMISSION LOG</span>
              {entries && <span className="vl-txlog-hdr-count">{entries.length} ENTRIES</span>}
              <button className="vl-txlog-hdr-close" onClick={() => setOpen(false)}>[ ESC ]</button>
            </div>

            {/* Feed */}
            <div className="vl-txlog-feed">
              {loading && (
                <div className="vl-txlog-status">RETRIEVING TRANSMISSIONS...</div>
              )}
              {entries?.length === 0 && (
                <div className="vl-txlog-status">{'// NO TRANSMISSIONS ARCHIVED'}</div>
              )}
              {entries && groupByDate(entries).map((group) => (
                <div key={group.date} className="vl-txlog-day">
                  {/* Day header — sticky while scrolling */}
                  <div className="vl-txlog-day-hdr">
                    <span className="vl-txlog-day-date">{group.date}</span>
                    <span className="vl-txlog-day-sep">{'//'}</span>
                    <span className="vl-txlog-day-count">
                      {group.items.length} TRANSMISSION{group.items.length !== 1 ? 'S' : ''}
                    </span>
                  </div>
                  {/* Entries for this day */}
                  {group.items.map((t) => {
                    const tod = timeOfDay(t.time);
                    return (
                      <div key={t.sha} className="vl-txlog-entry">
                        <div className="vl-txlog-meta">
                          <span className="vl-txlog-sha">[{t.sha}]</span>
                          {t.time && <span className="vl-txlog-time">{t.time}</span>}
                          {tod && (
                            <span className="vl-txlog-tod">
                              <span className="vl-txlog-tod-glyph">{tod.glyph}</span>
                              <span className="vl-txlog-tod-label">{tod.label}</span>
                            </span>
                          )}
                        </div>
                        <p className="vl-txlog-body">{t.transmission}</p>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─── Images ───────────────────────────────────────────────────────────────────

const IMAGES = [
  { src: '/images/Voidline_Sector_R-77_viewport_01.jpg',          showTitle: true,  portrait: false },
  { src: '/images/Voidline_Sector_R-77_viewport_02_portrait.jpg', showTitle: false, portrait: true  },
  { src: '/images/Voidline_Sector_R-77_viewport_03_portrait.jpg', showTitle: false, portrait: true  },
];

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function Page() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef    = useRef<HTMLImageElement>(null);
  const barsRef   = useRef<HTMLDivElement>(null);
  const time      = useClock();
  const radius    = useRadius();

  const [imgSrc,    setImgSrc]    = useState('');
  const [showTitle, setShowTitle] = useState(true);
  const [sectorStats, setSectorStats] = useState<SectorStats | null>(null);
  const [rooms,       setRooms]       = useState<RoomInfo[]>([]);
  const [panelVisible, setPanelVisible] = useState(false);
  const [signalVisible, setSignalVisible] = useState(false);
  const pingRef       = useRef<(() => void) | null>(null);
  const navigatingRef = useRef(false);
  const audio         = useAudio();

  const handleRooms = useCallback((r: RoomInfo[]) => {
    setRooms(r);
    setPanelVisible(true);
  }, []);

  useEffect(() => {
    const isMobile = window.matchMedia('(max-width:705px)').matches;
    const pool     = isMobile ? IMAGES.filter(i => i.portrait) : IMAGES;
    const chosen   = pool[Math.floor(Math.random() * pool.length)];
    setImgSrc(chosen.src);
    setShowTitle(chosen.showTitle);
  }, []);

  // Signal panel appears immediately after boot overlay clears (~1.5s)
  useEffect(() => {
    const id = setTimeout(() => setSignalVisible(true), 1500);
    return () => clearTimeout(id);
  }, []);

  useCanvasFX(canvasRef, imgRef, imgSrc, navigatingRef);
  useClassBars(barsRef);

  const sectorId = process.env.NEXT_PUBLIC_VOIDLINE_SECTOR ?? 'R-77';

  return (
    <>
      <style>{CSS}</style>
      <div id="vl-scene">
        <BootOverlay />
        {imgSrc && (
          // eslint-disable-next-line @next/next/no-img-element
          <img id="vl-bg" ref={imgRef} src={imgSrc} alt={`Voidline Sector ${sectorId}`} />
        )}
        <canvas id="vl-fx" ref={canvasRef} />

        <div id="vl-ui">
          <div className="vl-corner vl-tl" />
          <div className="vl-corner vl-tr" />
          <div className="vl-corner vl-bl" />
          <div className="vl-corner vl-br" />

          {/* Left rail: Signal panel + Sector Record trigger (desktop) */}
          {signalVisible && (
            <div id="vl-left-rail">
              <SignalPanel audio={audio} visible={true} />
              <TransmissionLog />
            </div>
          )}
          {/* Signal drawer — bottom sheet + FAB (mobile only, CSS-gated) */}
          <SignalDrawer audio={audio} visible={signalVisible} />

          {/* Aperture panel — right black bar */}
          <AperturePanel sectorId={sectorId} rooms={rooms} visible={panelVisible} onPing={() => pingRef.current?.()} navigatingRef={navigatingRef} />

          {/* Top bar */}
          <div id="vl-top">
            <span>VOIDLINE SYSTEMS &nbsp;//&nbsp; SECTOR {sectorId}</span>
            <div className="vl-top-r">
              <span id="vl-clock">{time}</span>
              <span className="vl-top-stat">
                OPEN APERTURES: <span className="vl-top-stat-val">{sectorStats ? sectorStats.openApertures : '—'}</span>
              </span>
              <span className="vl-top-stat">
                ACTIVE SIGNALS: <span className="vl-top-stat-val">{sectorStats ? sectorStats.activeSignals : '—'}</span>
              </span>
              <span className="vl-live">● LIVE</span>
            </div>
          </div>

          {/* Bottom-anchored: title + terminal */}
          <div id="vl-center">
            {showTitle && (
              <div id="vl-title">
                <div id="vl-title-name" className="vl-t-name vl-glitch">
                  VOIDLINE SECTOR {sectorId}
                </div>
              </div>
            )}

            <TerminalConsole sectorId={sectorId} onStats={setSectorStats} onRooms={handleRooms} pingRef={pingRef} navigatingRef={navigatingRef} />
            <TransmissionLog variant="mobile" />
          </div>

          {/* Bottom bar */}
          <div id="vl-bottom">
            <div className="vl-dc">
              <div className="vl-dl">Coordinates</div>
              <div className="vl-dv vl-glitch" id="vl-coords">51°28&apos;N · 00°07&apos;W</div>
            </div>
            <div className="vl-dc vl-dc-c">
              <div className="vl-dl">Anomaly Radius</div>
              <div className="vl-dv">{radius}</div>
              <div className="vl-status vl-status-red">EXPANDING</div>
            </div>
            <div className="vl-dc vl-dc-c">
              <div className="vl-dl">Atmospheric Pressure</div>
              <div className="vl-dv vl-glitch" id="vl-pressure">994.3 hPa</div>
            </div>
            <div className="vl-dc vl-dc-r">
              <div className="vl-dl">Classification</div>
              <div className="vl-class-bars" ref={barsRef}>
                <span /><span /><span /><span />
                <span /><span /><span /><span />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Share+Tech+Mono&family=Bebas+Neue&family=Crimson+Pro:ital,wght@1,300&display=swap');

:root {
  --vl-cyan:    #4ee8d4;
  --vl-cyan-d:  #1a7a6e;
  --vl-cyan-b:  #8fffef;
  --vl-dim:     #2a7a70;
  --vl-red:     #c0392b;
  --vl-red-b:   #e84e3a;
  --vl-green:   #29e87a;
  --vl-green-d: #0d6632;
  --vl-green-b: #6fffa8;
  --vl-mono:    'Share Tech Mono', monospace;
  --vl-display: 'Bebas Neue', sans-serif;
  --vl-italic:  'Crimson Pro', serif;
  /* Burnt orange — photon ring / inhabitant names */
  --vl-orange:   #d46a25;
  --vl-orange-b: #e88535;
  /* Arc Neon venue colors */
  --vl-arc-pink: #ff4fa3;
  --vl-arc-gold: #ffb347;
}

#vl-scene, #vl-scene * { box-sizing:border-box; margin:0; padding:0; }

#vl-scene {
  position:fixed; inset:0;
  background:#000;
  overflow:hidden;
  cursor:crosshair;
  font-family:var(--vl-mono);
}
#vl-bg  { display:block; width:100%; height:100%; object-fit:contain; object-position:center; }
#vl-fx  { position:absolute; inset:0; pointer-events:none; }
#vl-ui  { position:absolute; inset:0; pointer-events:none; }

/* ── Animations ── */
@keyframes vl-fadeUp  { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:translateY(0)} }
@keyframes vl-fadeIn  { from{opacity:0} to{opacity:1} }
@keyframes vl-blink   { 0%,100%{opacity:1} 50%{opacity:0.2} }
@keyframes vl-pulse   { 0%,100%{opacity:0.9} 50%{opacity:0.4} }
@keyframes vl-gfl {
  0%,93%  { opacity:1; clip-path:none; color:var(--vl-cyan); }
  94%     { opacity:0.4; clip-path:inset(25% 0 45% 0); color:var(--vl-cyan-b); }
  95%     { opacity:1; clip-path:none; }
  96%     { opacity:0.5; clip-path:inset(55% 0 15% 0); transform:translateX(2px); }
  97%,100%{ opacity:1; clip-path:none; transform:none; color:var(--vl-cyan); }
}
@keyframes vl-term-in {
  from { opacity:0; transform:translateY(8px); }
  to   { opacity:1; transform:translateY(0); }
}
@keyframes vl-cursor-blink {
  0%,49%   { opacity:1; }
  50%,100% { opacity:0; }
}

/* ── Corner brackets ── */
.vl-corner { position:absolute; width:44px; height:44px; opacity:0; animation:vl-fadeIn 0.5s ease forwards 0.3s; }
.vl-tl { top:18px;    left:18px;   border-top:2px solid var(--vl-cyan); border-left:2px solid var(--vl-cyan);   box-shadow:-2px -2px 8px rgba(78,232,212,0.3); }
.vl-tr { top:18px;    right:18px;  border-top:2px solid var(--vl-cyan); border-right:2px solid var(--vl-cyan);  box-shadow: 2px -2px 8px rgba(78,232,212,0.3); }
.vl-bl { bottom:18px; left:18px;   border-bottom:2px solid var(--vl-cyan); border-left:2px solid var(--vl-cyan);  box-shadow:-2px  2px 8px rgba(78,232,212,0.3); }
.vl-br { bottom:18px; right:18px;  border-bottom:2px solid var(--vl-cyan); border-right:2px solid var(--vl-cyan); box-shadow: 2px  2px 8px rgba(78,232,212,0.3); }

/* ── Top bar ── */
#vl-top {
  position:absolute; top:0; left:0; right:0;
  padding:28px 36px 20px;
  display:flex; justify-content:space-between; align-items:center;
  font-size:14px; letter-spacing:0.24em; text-transform:uppercase;
  color:var(--vl-cyan);
  border-bottom:1px solid rgba(78,232,212,0.15);
  opacity:0; animation:vl-fadeUp 0.8s ease forwards 0.5s;
  pointer-events:none;
}
.vl-top-r { display:flex; gap:22px; align-items:center; }
.vl-top-stat { font-size:11px; letter-spacing:0.18em; color:var(--vl-cyan); opacity:0.85; white-space:nowrap; }
.vl-top-stat-val { color:var(--vl-cyan-b); text-shadow:0 0 8px rgba(78,232,212,0.6); }
.vl-live { color:var(--vl-red-b); font-size:15px; letter-spacing:0.2em; animation:vl-blink 2.4s ease-in-out infinite; text-shadow:0 0 12px rgba(232,78,58,0.7); }
#vl-clock { letter-spacing:0.15em; color:var(--vl-cyan); }

/* ── Centre column — bottom anchored ── */
#vl-center {
  position:absolute; top:0; left:0; right:0; bottom:0;
  display:flex; flex-direction:column;
  align-items:center; justify-content:flex-end;
  gap:18px;
  padding:90px 24px 118px;
  pointer-events:none;
}

/* ── Title block ── */
#vl-title {
  display:flex; flex-direction:column; align-items:center; text-align:center;
  opacity:0; animation:vl-fadeUp 1.1s ease forwards 1.2s;
  flex-shrink:0;
}
.vl-t-name    { font-family:var(--vl-display); font-size:clamp(36px,5vw,72px); line-height:0.92; color:var(--vl-cyan); letter-spacing:0.06em; text-shadow:0 0 12px rgba(78,232,212,0.9),0 0 35px rgba(78,232,212,0.55),0 0 80px rgba(78,232,212,0.25); }
.vl-glitch    { animation:vl-gfl 7s steps(1) infinite; }

/* ══════════════════════════════════════════════
   TERMINAL CONSOLE — holographic, translucent bg
   ══════════════════════════════════════════════ */

#vl-terminal {
  pointer-events:all;
  width:100%;
  max-width:520px;
  background:rgba(2, 18, 8, 0.38);
  backdrop-filter:blur(3px) saturate(1.15);
  -webkit-backdrop-filter:blur(3px) saturate(1.15);
  border:1px solid rgba(41,232,122,0.18);
  box-shadow:
    0 0 0 1px rgba(41,232,122,0.04),
    0 0 32px rgba(41,232,122,0.06);
  transition:border-color 0.15s ease, box-shadow 0.15s ease;
  display:flex; flex-direction:column;
  height:168px;
  cursor:text;
}
#vl-terminal.vl-term-focused {
  border-color:rgba(41,232,122,0.5);
  box-shadow:
    0 0 0 1px rgba(41,232,122,0.08),
    0 0 22px rgba(41,232,122,0.14);
}

/* Header bar */
.vl-term-header {
  display:flex; align-items:center; justify-content:space-between;
  padding:6px 16px !important;
  border-bottom:1px solid rgba(41,232,122,0.12);
  background:rgba(41,232,122,0.03);
  flex-shrink:0;
}
.vl-term-header-id {
  font-size:9px; letter-spacing:0.25em; color:var(--vl-green);
  text-transform:uppercase; opacity:0.7;
  text-shadow:0 0 6px rgba(41,232,122,0.3);
}
.vl-term-header-sig {
  font-size:8px; letter-spacing:0.2em; color:var(--vl-green);
  text-shadow:0 0 8px rgba(41,232,122,0.5);
  animation:vl-pulse 2.8s ease-in-out infinite;
}

/* Output area */
.vl-term-output {
  flex:1;
  overflow-y:auto;
  padding:8px 16px 4px !important;
  display:flex; flex-direction:column; gap:3px;
  scrollbar-width:none;
}
.vl-term-output::-webkit-scrollbar { display:none; }

.vl-term-line      { font-size:11px; letter-spacing:0.12em; color:var(--vl-green); line-height:1.5; text-shadow:0 0 8px rgba(41,232,122,0.5); white-space:pre-wrap; word-break:break-all; }
.vl-term-line-boot { color:var(--vl-green); }
.vl-term-line-dim  { color:var(--vl-green); opacity:0.6; }
.vl-term-line-err  { color:var(--vl-red-b); text-shadow:0 0 6px rgba(232,78,58,0.3); }

/* Prompt line */
.vl-term-prompt-line {
  display:flex; align-items:center;
  padding:6px 16px 10px !important;
  border-top:1px solid rgba(41,232,122,0.1);
  gap:0;
}
.vl-term-prompt-path {
  color:var(--vl-green); font-size:11px; letter-spacing:0.06em;
  text-shadow:0 0 6px rgba(41,232,122,0.28);
  flex-shrink:0; user-select:none; white-space:nowrap;
}
.vl-term-prompt-char {
  color:var(--vl-green); font-size:12px; letter-spacing:0.05em;
  text-shadow:0 0 8px rgba(41,232,122,0.5);
  flex-shrink:0; user-select:none; margin-right:6px;
}
.vl-term-input-wrap {
  flex:1; position:relative;
  display:flex; align-items:center;
  min-width:0;
}
/* Invisible sizer — matches input font exactly, drives the wrap width */
.vl-term-input-sizer {
  font-family:var(--vl-mono); font-size:12px; letter-spacing:0.1em;
  visibility:hidden; white-space:pre;
  height:0; overflow:hidden;
  pointer-events:none; user-select:none;
  flex-shrink:0;
}
.vl-term-input {
  position:absolute; left:0; top:0; bottom:0;
  width:100%;
  background:transparent; border:none; outline:none;
  color:var(--vl-green);
  font-family:var(--vl-mono); font-size:12px; letter-spacing:0.1em;
  caret-color:transparent;
  padding:0;
  text-shadow:0 0 6px rgba(41,232,122,0.4);
}
.vl-term-input:disabled { opacity:0.5; cursor:not-allowed; }

/* Block cursor — sits inline after typed text via flex row */
.vl-term-cursor {
  display:inline-block;
  width:8px; height:14px;
  background:var(--vl-green);
  box-shadow:0 0 8px rgba(41,232,122,0.9);
  animation:vl-cursor-blink 1.06s step-start infinite;
  flex-shrink:0;
  align-self:center;
  margin-left:1px;
}



/* ══════════════════════════════════════════════
   APERTURE PANEL — cyan HUD, rail-mounted
   ══════════════════════════════════════════════ */

/* Per-room glitch — cyan base, independent timing per room via animationDelay */
@keyframes vl-ap-glitch {
  0%,88%  { opacity:1; clip-path:none; color:var(--vl-cyan); transform:none; }
  89%     { opacity:0.5; clip-path:inset(20% 0 50% 0); color:var(--vl-cyan-b); transform:translateX(3px); }
  90%     { opacity:1; clip-path:none; transform:none; }
  91%     { opacity:0.6; clip-path:inset(60% 0 10% 0); transform:translateX(-2px); color:var(--vl-cyan-b); }
  92%,100%{ opacity:1; clip-path:none; transform:none; color:var(--vl-cyan); }
}
/* Arc Neon — pink/gold variant */
@keyframes vl-ap-glitch-arc {
  0%,88%  { opacity:1; clip-path:none; color:var(--vl-arc-pink); transform:none; }
  89%     { opacity:0.5; clip-path:inset(20% 0 50% 0); color:var(--vl-arc-gold); transform:translateX(3px); }
  90%     { opacity:1; clip-path:none; transform:none; }
  91%     { opacity:0.6; clip-path:inset(60% 0 10% 0); transform:translateX(-2px); color:var(--vl-arc-gold); }
  92%,100%{ opacity:1; clip-path:none; transform:none; color:var(--vl-arc-pink); }
}
@keyframes vl-ap-slidein {
  from { opacity:0; transform:translateX(20px); }
  to   { opacity:1; transform:translateX(0); }
}

#vl-aperture-panel {
  pointer-events:all;
  position:absolute;
  /* TR corner bracket: top:18px, height:44px, right:18px.
     Panel's right border sits on the same x-axis. Clear vertical gap. */
  top:80px;
  right:18px;
  width:max-content; min-width:310px; max-width:420px;
  background:rgba(0,8,12,0.82);
  backdrop-filter:blur(2px);
  -webkit-backdrop-filter:blur(2px);
  border:1px solid rgba(78,232,212,0.25);
  /* Right border matches the corner bracket: 2px solid cyan with glow */
  border-right:2px solid var(--vl-cyan);
  box-shadow:
    2px 0 8px rgba(78,232,212,0.3),
    -4px 0 20px rgba(78,232,212,0.04),
    0 4px 20px rgba(78,232,212,0.04);
  display:flex; flex-direction:column;
  animation:vl-ap-slidein 0.5s cubic-bezier(0.16,1,0.3,1) forwards;
}

.vl-ap-header {
  display:flex; align-items:center; justify-content:space-between;
  padding:8px 18px 8px 0 !important;
  border-bottom:1px solid rgba(78,232,212,0.12);
  gap:0;
}
.vl-ap-refresh {
  background:transparent; border:none;
  border-right:1px solid rgba(78,232,212,0.15);
  font-family:var(--vl-mono); font-size:16px;
  color:var(--vl-cyan); cursor:pointer;
  padding:0 13px !important; margin:0; line-height:1;
  text-shadow:0 0 6px rgba(78,232,212,0.4);
  transition:color 0.15s ease, text-shadow 0.15s ease, background 0.15s ease;
  flex-shrink:0;
  align-self:stretch;
  display:flex; align-items:center;
}
.vl-ap-refresh:hover {
  color:var(--vl-orange-b);
  text-shadow:0 0 10px rgba(232,133,53,0.7);
  background:rgba(232,133,53,0.04);
}
.vl-ap-refresh:active {
  transform:rotate(90deg);
}
.vl-ap-title {
  font-size:12px; letter-spacing:0.28em; color:var(--vl-cyan);
  text-transform:uppercase; opacity:0.85;
  text-shadow:0 0 6px rgba(78,232,212,0.3);
  flex:1; padding-left:12px !important;
}
.vl-ap-count {
  font-size:14px; letter-spacing:0.08em;
  color:var(--vl-cyan-b);
  text-shadow:0 0 8px rgba(78,232,212,0.7);
}

.vl-ap-list {
  display:flex; flex-direction:column;
  padding:5px 0 !important;
}

.vl-ap-empty {
  font-size:11px; letter-spacing:0.2em;
  color:var(--vl-cyan-d);
  padding:10px 18px !important;
  line-height:1.5;
}

.vl-ap-room {
  display:flex; flex-direction:column; gap:0;
  padding:9px 18px 8px !important;
  background:transparent; border:none;
  font-family:var(--vl-mono);
  cursor:pointer; text-align:left; width:100%;
  border-left:3px solid transparent;
  border-bottom:1px solid rgba(78,232,212,0.06);
  transition:background 0.12s ease, border-color 0.12s ease;
}
.vl-ap-room:last-child { border-bottom:none; }
.vl-ap-room:hover:not(:disabled) {
  background:rgba(78,232,212,0.05);
  border-left-color:var(--vl-cyan);
}
.vl-ap-room:disabled { opacity:0.5; cursor:not-allowed; }

/* Arc Neon special */
.vl-ap-room-arc { border-bottom:1px solid rgba(255,79,163,0.1); }
.vl-ap-room-arc:hover:not(:disabled) {
  background:rgba(255,79,163,0.05);
  border-left-color:var(--vl-arc-pink);
}

.vl-ap-room-header {
  display:flex; align-items:baseline; gap:8px;
}

.vl-ap-indicator {
  font-size:10px; color:var(--vl-cyan); flex-shrink:0;
  text-shadow:0 0 5px rgba(78,232,212,0.6);
}
.vl-ap-room-arc .vl-ap-indicator {
  color:var(--vl-arc-gold);
  text-shadow:0 0 8px rgba(255,179,71,0.8);
  font-size:13px;
}

.vl-ap-glitch {
  animation:vl-ap-glitch 8s steps(1) infinite;
}
.vl-ap-room-arc .vl-ap-glitch {
  animation-name:vl-ap-glitch-arc;
}

.vl-ap-name {
  font-size:13px; letter-spacing:0.12em;
  color:var(--vl-cyan); flex:1;
  text-shadow:0 0 6px rgba(78,232,212,0.35);
  overflow-wrap:break-word; word-break:break-all;
}
.vl-ap-room:hover:not(:disabled) .vl-ap-name {
  color:var(--vl-cyan-b);
  text-shadow:0 0 10px rgba(78,232,212,0.7);
}
.vl-ap-room-arc .vl-ap-name {
  color:var(--vl-arc-pink);
  text-shadow:0 0 8px rgba(255,79,163,0.5), 0 0 20px rgba(255,179,71,0.2);
}
.vl-ap-room-arc:hover:not(:disabled) .vl-ap-name {
  text-shadow:0 0 12px rgba(255,79,163,0.8), 0 0 28px rgba(255,179,71,0.35);
}

.vl-ap-inh-count {
  font-size:11px; letter-spacing:0.06em;
  color:var(--vl-green); opacity:1; flex-shrink:0;
  text-shadow:0 0 6px rgba(41,232,122,0.5);
  white-space:nowrap;
}
.vl-ap-room-arc .vl-ap-inh-count { color:var(--vl-arc-gold); }
.vl-ap-inh-label { font-size:10px; letter-spacing:0.15em; }

/* Participant name glitch — console green base, cyan flash (normal rooms) */
@keyframes vl-ap-glitch-name {
  0%,88%  { color:var(--vl-green); transform:none; }
  89%     { color:var(--vl-cyan-b); transform:translateX(2px); }
  90%     { color:var(--vl-green); transform:none; }
  91%     { color:var(--vl-cyan); transform:translateX(-1px); }
  92%,100%{ color:var(--vl-green); transform:none; }
}
/* Arc Neon participant glitch — gold base, pink flash */
@keyframes vl-ap-glitch-name-arc {
  0%,88%  { color:var(--vl-arc-gold); transform:none; }
  89%     { color:var(--vl-arc-pink); transform:translateX(2px); }
  90%     { color:var(--vl-arc-gold); transform:none; }
  91%     { color:var(--vl-arc-pink); transform:translateX(-1px); }
  92%,100%{ color:var(--vl-arc-gold); transform:none; }
}

.vl-ap-participants {
  display:flex; flex-direction:column; gap:4px;
  padding-top:5px !important;
  padding-left:18px !important;
}
.vl-ap-participant {
  display:flex; align-items:center; gap:6px;
  font-size:10px; letter-spacing:0.14em;
  color:var(--vl-green);
  text-shadow:0 0 6px rgba(41,232,122,0.4);
  animation:vl-ap-glitch-name 8s steps(1) infinite;
}
.vl-ap-participant-dot {
  font-size:10px; flex-shrink:0;
  color:var(--vl-green);
  text-shadow:0 0 6px rgba(41,232,122,0.5);
  animation:vl-pulse 2.8s ease-in-out infinite;
}
.vl-ap-room-arc .vl-ap-participant {
  color:var(--vl-arc-gold);
  text-shadow:0 0 6px rgba(255,179,71,0.5);
  animation-name:vl-ap-glitch-name-arc;
}
.vl-ap-room-arc .vl-ap-participant-dot {
  color:var(--vl-arc-gold);
  text-shadow:0 0 6px rgba(255,179,71,0.6);
}

.vl-ap-footer {
  padding:8px 18px 10px !important;
  border-top:1px solid rgba(78,232,212,0.1);
  font-size:9px; letter-spacing:0.25em;
  color:var(--vl-cyan-d); text-transform:uppercase; opacity:0.6;
}

/* ══════════════════════════════════════════════
   SIGNAL PANEL — left-side audio control, mirrors aperture panel
   ══════════════════════════════════════════════ */

@keyframes vl-sp-slidein {
  from { opacity:0; transform:translateX(-20px); }
  to   { opacity:1; transform:translateX(0); }
}

#vl-signal-panel {
  pointer-events:all;
  width:100%;
  background:rgba(0,8,12,0.82);
  backdrop-filter:blur(2px);
  -webkit-backdrop-filter:blur(2px);
  border:1px solid rgba(78,232,212,0.25);
  border-left:2px solid var(--vl-cyan);
  box-shadow:
    -2px 0 8px rgba(78,232,212,0.3),
    4px 0 20px rgba(78,232,212,0.04),
    0 4px 20px rgba(78,232,212,0.04);
  display:flex; flex-direction:column;
  animation:vl-sp-slidein 0.5s cubic-bezier(0.16,1,0.3,1) forwards;
  transition:width 0.25s ease;
}
#vl-signal-panel.vl-sp-collapsed {
  width:100%;
}

/* Header: title row + collapse toggle */
.vl-sp-header {
  display:flex; align-items:center; justify-content:space-between;
  padding:8px 6px 8px 14px !important;
  border-bottom:1px solid rgba(78,232,212,0.12);
}
.vl-sp-collapsed .vl-sp-header { border-bottom:none; }
.vl-sp-title {
  font-size:9px; letter-spacing:0.1em;
  color:var(--vl-cyan-b);
  text-shadow:0 0 8px rgba(78,232,212,0.6);
  text-transform:none;
  white-space:nowrap; overflow:hidden; text-overflow:ellipsis;
  flex:1; min-width:0;
}
.vl-sp-collapse-btn {
  background:transparent; border:none;
  font-family:var(--vl-mono);
  font-size:10px; color:var(--vl-cyan-d);
  cursor:pointer; padding:2px 6px !important; margin:0;
  flex-shrink:0;
  opacity:0.5; transition:opacity 0.15s ease, color 0.15s ease;
}
.vl-sp-collapse-btn:hover {
  opacity:1; color:var(--vl-cyan-b);
}

/* Controls row: transport left, volume right */
.vl-sp-controls {
  display:flex; align-items:center; justify-content:space-between;
  padding:0 0 0 10px !important;
  border-bottom:1px solid rgba(78,232,212,0.08);
}
.vl-sp-transport {
  display:flex; align-items:center; flex-shrink:0;
}

/* All transport buttons share base style */
.vl-sp-btn {
  background:transparent; border:none;
  font-family:var(--vl-mono);
  color:var(--vl-cyan); cursor:pointer;
  padding:0 5px !important; margin:0;
  align-self:stretch; display:flex; align-items:center; justify-content:center;
  text-shadow:0 0 6px rgba(78,232,212,0.4);
  transition:color 0.12s ease, text-shadow 0.12s ease;
  line-height:1;
}

/* Prev / next — fixed width, no layout shift */
.vl-sp-prev, .vl-sp-next {
  font-size:13px; letter-spacing:-0.05em;
  width:26px;
}
.vl-sp-prev:hover, .vl-sp-next:hover {
  color:var(--vl-cyan-b);
  text-shadow:0 0 10px rgba(78,232,212,0.8);
}

/* Play/pause — fixed width prevents layout shift on symbol swap. Always cyan. */
.vl-sp-playpause {
  font-size:14px; letter-spacing:-0.1em;
  width:28px;
  color:var(--vl-cyan-b);
  text-shadow:0 0 10px rgba(78,232,212,0.9);
}
.vl-sp-playpause.vl-sp-is-playing { letter-spacing:0; }
.vl-sp-playpause:hover { filter:brightness(1.3); }

/* CSS pause icon — two solid rectangles, no font rendering surprises */
.vl-sp-pause-icon {
  display:inline-flex; align-items:center; gap:3px;
  height:14px;
}
.vl-sp-pause-icon span {
  display:block; width:3px; height:12px;
  background:var(--vl-cyan-b);
  box-shadow:0 0 6px rgba(78,232,212,0.8);
  border-radius:1px;
}

/* Volume — right side of controls row */
.vl-sp-vol-row {
  display:flex; align-items:center; gap:6px;
  padding:7px 16px 7px 8px !important;
  flex:1; min-width:0;
  border-left:1px solid rgba(78,232,212,0.12);
  overflow:hidden;
}
.vl-sp-vol-label {
  font-size:8px; letter-spacing:0.3em; color:var(--vl-cyan-b);
  text-shadow:0 0 6px rgba(78,232,212,0.6);
  text-transform:uppercase; flex-shrink:0;
  user-select:none;
}
.vl-sp-vol {
  -webkit-appearance:none; appearance:none;
  width:0; flex:1; height:3px;
  background:rgba(78,232,212,0.18);
  border-radius:2px; outline:none; cursor:pointer;
  margin-right:6px;
  max-width:calc(100% - 40px);
}
.vl-sp-vol::-webkit-slider-thumb {
  -webkit-appearance:none; appearance:none;
  width:10px; height:10px; border-radius:50%;
  background:var(--vl-cyan-b);
  box-shadow:0 0 6px rgba(78,232,212,0.8);
  cursor:pointer;
}
.vl-sp-vol::-moz-range-thumb {
  width:10px; height:10px; border-radius:50%; border:none;
  background:var(--vl-cyan-b);
  box-shadow:0 0 6px rgba(78,232,212,0.8);
  cursor:pointer;
}

/* Filename — isolated line below vol row */
.vl-sp-track {
  padding:10px 18px 10px !important;
}
.vl-sp-filename {
  font-size:11px; letter-spacing:0.1em;
  color:var(--vl-cyan-b);
  text-shadow:0 0 8px rgba(78,232,212,0.45);
  word-break:break-all; line-height:1.6;
}

/* Footer: attribution */
.vl-sp-footer {
  padding:6px 18px 8px !important;
  border-top:1px solid rgba(78,232,212,0.1);
}
.vl-sp-credit {
  font-size:9px; letter-spacing:0.18em;
  color:var(--vl-cyan-d); text-transform:uppercase; opacity:0.4;
}
/* Signal drawer + FAB: hidden on desktop, shown on mobile */
.vl-sig-fab, .vl-sig-backdrop { display:none; }

/* Mid-width: top bar wraps to two lines — push both panels below it */
@media (max-width:1050px) {
  #vl-aperture-panel { top:110px; }
  #vl-left-rail      { top:110px; }
}
/* Mobile: hide left rail (drawer handles signal panel), show drawer + FAB */
@media (max-width:705px) {
  #vl-aperture-panel { top:56px; right:8px; min-width:220px; max-width:280px; }
  #vl-left-rail      { display:none !important; }
  .vl-ap-name { font-size:11px; }
  .vl-ap-participant { font-size:8px; }

  /* ── Floating SIG button ── */
  .vl-sig-fab {
    display:flex; align-items:center; justify-content:center;
    pointer-events:all;
    position:absolute;
    top:56px; left:8px;
    width:44px; height:44px;
    background:rgba(0,8,12,0.85);
    backdrop-filter:blur(4px);
    -webkit-backdrop-filter:blur(4px);
    border:1px solid rgba(78,232,212,0.35);
    border-left:2px solid var(--vl-cyan);
    border-radius:4px;
    font-family:var(--vl-mono);
    font-size:10px; letter-spacing:0.12em;
    color:var(--vl-cyan);
    text-shadow:0 0 8px rgba(78,232,212,0.5);
    cursor:pointer;
    box-shadow:
      -2px 0 8px rgba(78,232,212,0.2),
      0 0 12px rgba(78,232,212,0.06);
    animation:vl-sp-slidein 0.4s cubic-bezier(0.16,1,0.3,1) forwards;
    z-index:50;
    transition:border-color 0.2s ease, box-shadow 0.2s ease;
  }
  .vl-sig-fab:active {
    background:rgba(0,20,16,0.9);
  }
  .vl-sig-fab-playing {
    border-color:var(--vl-cyan);
    box-shadow:
      -2px 0 10px rgba(78,232,212,0.35),
      0 0 16px rgba(78,232,212,0.12);
  }

  /* Mini EQ bars animation for playing state */
  .vl-sig-fab-eq {
    display:flex; align-items:flex-end; gap:2px; height:16px;
  }
  .vl-sig-fab-eq span {
    display:block; width:3px;
    background:var(--vl-cyan-b);
    box-shadow:0 0 4px rgba(78,232,212,0.6);
    border-radius:1px;
  }
  .vl-sig-fab-eq span:nth-child(1) { animation:vl-eq 0.8s ease-in-out infinite; height:6px; }
  .vl-sig-fab-eq span:nth-child(2) { animation:vl-eq 0.8s ease-in-out 0.2s infinite; height:10px; }
  .vl-sig-fab-eq span:nth-child(3) { animation:vl-eq 0.8s ease-in-out 0.4s infinite; height:4px; }

  @keyframes vl-eq {
    0%,100% { height:4px; }
    50%     { height:14px; }
  }

  .vl-sig-fab-note {
    font-size:18px; line-height:1;
    text-shadow:0 0 8px rgba(78,232,212,0.6);
  }

  /* ── Backdrop ── */
  .vl-sig-backdrop {
    display:flex;
    position:fixed; inset:0; z-index:100;
    background:rgba(0,0,0,0.65);
    align-items:flex-end;
    justify-content:center;
    pointer-events:all;
    animation:vl-fadeIn 0.15s ease;
  }

  /* ── Drawer panel ── */
  .vl-sig-drawer {
    width:100%;
    max-width:420px;
    background:rgba(0,6,8,0.97);
    backdrop-filter:blur(12px);
    -webkit-backdrop-filter:blur(12px);
    border:1px solid rgba(78,232,212,0.4);
    border-bottom:none;
    border-top:2px solid var(--vl-cyan);
    border-radius:10px 10px 0 0;
    box-shadow:
      0 -4px 24px rgba(78,232,212,0.15),
      0 -1px 40px rgba(78,232,212,0.06),
      inset 0 1px 0 rgba(78,232,212,0.08);
    padding-bottom:env(safe-area-inset-bottom, 12px);
    animation:vl-drawer-up 0.3s cubic-bezier(0.16,1,0.3,1) forwards;
  }
  @keyframes vl-drawer-up {
    from { opacity:0; transform:translateY(100%); }
    to   { opacity:1; transform:translateY(0); }
  }

  /* Drag handle indicator */
  .vl-sig-drawer-handle {
    width:36px; height:3px;
    background:rgba(78,232,212,0.3);
    border-radius:2px;
    margin:10px auto 4px;
  }

  /* Drawer overrides: full-width layout, no slide-in animation */
  .vl-sig-drawer .vl-sp-header {
    padding:8px 16px 8px 18px !important;
  }
  .vl-sig-drawer .vl-sp-collapse-btn { font-size:13px; }
  .vl-sig-drawer .vl-sp-vol-row {
    padding:7px 18px 7px 10px !important;
  }
  .vl-sig-drawer .vl-sp-track {
    padding:10px 18px !important;
  }
  .vl-sig-drawer .vl-sp-footer {
    padding:6px 18px 10px !important;
  }
}

/* ── Bottom bar ── */
#vl-bottom {
  position:absolute; bottom:0; left:0; right:0;
  padding:16px 36px 26px;
  display:flex; justify-content:space-between; align-items:flex-end;
  border-top:1px solid rgba(78,232,212,0.15);
  opacity:0; animation:vl-fadeUp 0.8s ease forwards 2.4s;
  pointer-events:none;
}
.vl-dc   { display:flex; flex-direction:column; gap:5px; }
.vl-dc-c { align-items:center; }
.vl-dc-r { align-items:flex-end; }
.vl-dl   { font-size:11px; letter-spacing:0.3em; text-transform:uppercase; color:var(--vl-cyan); opacity:0.75; }
.vl-dv   { font-size:18px; letter-spacing:0.08em; color:var(--vl-cyan); text-shadow:0 0 10px rgba(78,232,212,0.35); }
.vl-status     { font-size:11px; letter-spacing:0.25em; text-transform:uppercase; color:var(--vl-cyan); border:1px solid var(--vl-cyan); padding:3px 10px; margin-top:2px; animation:vl-pulse 3s ease-in-out infinite; }
.vl-status-red { color:var(--vl-red-b); border-color:var(--vl-red-b); text-shadow:0 0 8px rgba(232,78,58,0.5); box-shadow:0 0 6px rgba(232,78,58,0.2); }

/* Classification bars */
.vl-class-bars      { display:flex; align-items:flex-end; gap:3px; width:100%; height:16px; margin-top:4px; }
.vl-class-bars span { flex:1; height:100%; background:var(--vl-cyan); border-radius:1px; opacity:0.8; transition:height 0.4s ease-in; box-shadow:0 0 4px rgba(78,232,212,0.3); }

/* ── Responsive ── */
@media (max-width: 705px) {
  #vl-top { justify-content:flex-end; padding:16px 20px 12px; }
  #vl-top > span:first-child { display:none; }
  .vl-top-r { gap:10px; }
  .vl-top-stat { font-size:9px; letter-spacing:0.1em; }
  .vl-live { font-size:13px; }
  #vl-clock { font-size:12px; }

  .vl-corner { width:28px; height:28px; }
  .vl-tl { top:8px;    left:8px;  }
  .vl-tr { top:8px;    right:8px; }
  .vl-bl { bottom:8px; left:8px;  }
  .vl-br { bottom:8px; right:8px; }

  #vl-center { padding:70px 16px 136px; gap:14px; }

  .vl-t-name    { font-size:clamp(28px,10vw,52px); }

  #vl-bottom {
    display:grid; grid-template-columns:1fr 1fr; gap:14px 10px;
    padding:14px 20px 18px; align-items:start;
  }
  #vl-bottom .vl-dc:nth-child(1) { align-items:flex-start; }
  #vl-bottom .vl-dc:nth-child(2) { align-items:flex-end; }
  #vl-bottom .vl-dc:nth-child(3) { align-items:flex-start; }
  #vl-bottom .vl-dc:nth-child(4) { align-items:flex-end; }
  .vl-dl { font-size:9px;  letter-spacing:0.2em; }
  .vl-dv { font-size:14px; }
  .vl-status { font-size:9px; padding:2px 7px; }

  #vl-terminal { max-width:100%; height:148px; }

}

/* ══════════════════════════════════════════════
   LEFT RAIL — Signal Panel + Sector Record
   ══════════════════════════════════════════════ */

#vl-left-rail {
  pointer-events:none;
  position:absolute;
  top:80px; left:18px;
  display:flex; flex-direction:column; gap:8px;
  width:255px;
}
/* Override signal panel when hosted in the rail (no longer self-positioned) */
#vl-left-rail #vl-signal-panel {
  position:relative;
  top:auto; left:auto;
  width:100%;
}
#vl-left-rail #vl-signal-panel.vl-sp-collapsed { width:100%; }

/* Compact sector record trigger */
.vl-sr-trigger {
  pointer-events:all;
  display:flex; align-items:center;
  width:100%;
  overflow:hidden;
  background:rgba(0,8,12,0.82);
  backdrop-filter:blur(2px);
  -webkit-backdrop-filter:blur(2px);
  border:1px solid rgba(78,232,212,0.25);
  border-left:2px solid var(--vl-cyan);
  box-shadow:-2px 0 8px rgba(78,232,212,0.3), 4px 0 20px rgba(78,232,212,0.04);
  font-family:var(--vl-mono);
  cursor:pointer; text-align:left;
  padding:0 !important;
  animation:vl-sp-slidein 0.5s cubic-bezier(0.16,1,0.3,1) 0.08s both;
  transition:background 0.12s ease, box-shadow 0.12s ease;
}
.vl-sr-trigger:hover {
  background:rgba(0,14,10,0.92);
  box-shadow:-3px 0 12px rgba(78,232,212,0.42), 4px 0 24px rgba(78,232,212,0.06);
}
.vl-sr-path {
  font-size:9px; letter-spacing:0.1em;
  color:var(--vl-cyan-d); opacity:0.65;
  padding:9px 12px !important;
  border-right:1px solid rgba(78,232,212,0.12);
  flex-shrink:0; white-space:nowrap;
  align-self:stretch; display:flex; align-items:center;
}
.vl-sr-label {
  font-size:10px; letter-spacing:0.22em;
  color:var(--vl-cyan);
  text-shadow:0 0 6px rgba(78,232,212,0.35);
  flex:1; padding-left:12px !important; white-space:nowrap;
  transition:color 0.12s ease, text-shadow 0.12s ease;
}
.vl-sr-trigger:hover .vl-sr-label {
  color:var(--vl-cyan-b);
  text-shadow:0 0 10px rgba(78,232,212,0.65);
}
.vl-sr-count {
  font-size:10px; letter-spacing:0.1em;
  color:var(--vl-cyan-d); opacity:0.6;
  padding-right:4px !important; flex-shrink:0;
}
.vl-sr-arrow {
  font-size:11px; color:var(--vl-cyan-d); opacity:0.5;
  padding:9px 13px 9px 5px !important; flex-shrink:0;
  transition:color 0.12s ease, opacity 0.12s ease, transform 0.12s ease;
}
.vl-sr-trigger:hover .vl-sr-arrow { color:var(--vl-cyan); opacity:1; transform:translateX(2px); }

/* Mobile text trigger — below terminal, desktop-hidden */
.vl-sr-mobile-btn {
  display:none;
  pointer-events:all;
  background:transparent; border:none;
  font-family:var(--vl-mono);
  font-size:9px; letter-spacing:0.28em;
  color:var(--vl-cyan-d); opacity:0.6;
  cursor:pointer; padding:5px 4px;
  text-transform:uppercase;
  transition:opacity 0.15s ease, color 0.15s ease;
}
.vl-sr-mobile-btn:hover { opacity:1; color:var(--vl-cyan); text-shadow:0 0 6px rgba(78,232,212,0.4); }
@media (max-width:705px) { .vl-sr-mobile-btn { display:block; } }

/* Fullscreen overlay backdrop */
@keyframes vl-txlog-in {
  0%   { opacity:0; }
  12%  { opacity:0.55; }
  18%  { opacity:0.1; }
  28%  { opacity:0.9; }
  34%  { opacity:0.65; }
  42%  { opacity:1; }
  100% { opacity:1; }
}
@keyframes vl-txlog-panel-up {
  from { transform:translateY(18px); opacity:0; }
  to   { transform:translateY(0);    opacity:1; }
}

.vl-txlog-overlay {
  pointer-events:all;
  position:fixed; inset:0; z-index:200;
  background:rgba(0,3,2,0.9);
  backdrop-filter:blur(3px);
  -webkit-backdrop-filter:blur(3px);
  display:flex; align-items:center; justify-content:center;
  animation:vl-txlog-in 0.45s steps(1) forwards;
  padding:24px 16px;
}

.vl-txlog-panel {
  width:min(860px, 96vw);
  max-height:88vh;
  background:rgba(0,6,4,0.98);
  border:1px solid rgba(78,232,212,0.22);
  border-top:2px solid var(--vl-cyan);
  box-shadow:
    0 0 0 1px rgba(78,232,212,0.04),
    0 -4px 40px rgba(78,232,212,0.08),
    0  8px 40px rgba(0,0,0,0.7);
  display:flex; flex-direction:column;
  animation:vl-txlog-panel-up 0.35s cubic-bezier(0.16,1,0.3,1) 0.1s both;
  overflow:hidden;
  position:relative;
}
/* CRT: rolling scanlines */
@keyframes vl-txlog-scanroll {
  0%   { background-position: 0 0; }
  100% { background-position: 0 100px; }
}
.vl-txlog-panel::before {
  content:''; position:absolute; inset:0;
  background:repeating-linear-gradient(
    0deg,
    transparent,              transparent 2px,
    rgba(255,255,255,0.04) 2px, rgba(255,255,255,0.04) 4px
  );
  pointer-events:none; z-index:20;
  animation:vl-txlog-scanroll 10s linear infinite;
}
/* CRT: static film grain */
.vl-txlog-panel::after {
  content:''; position:absolute; inset:0;
  opacity:0.07; pointer-events:none; z-index:19;
  background-image:url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
  background-size:256px;
}

/* Panel header */
.vl-txlog-hdr {
  display:flex; align-items:center; gap:0;
  padding:9px 20px 9px 0 !important;
  border-bottom:1px solid rgba(78,232,212,0.14);
  background:rgba(78,232,212,0.02);
  flex-shrink:0;
}
.vl-txlog-hdr-path {
  font-size:9px; letter-spacing:0.1em;
  color:var(--vl-cyan-d); opacity:0.85;
  padding:0 14px !important;
  border-right:1px solid rgba(78,232,212,0.12);
  flex-shrink:0; white-space:nowrap;
  align-self:stretch; display:flex; align-items:center;
}
.vl-txlog-hdr-title {
  font-size:11px; letter-spacing:0.3em;
  color:var(--vl-cyan);
  text-shadow:0 0 6px rgba(78,232,212,0.35);
  flex:1; padding-left:16px !important;
}
.vl-txlog-hdr-count {
  font-size:9px; letter-spacing:0.22em;
  color:var(--vl-cyan-d); opacity:1;
  flex-shrink:0; padding-right:16px !important;
  white-space:nowrap;
}
.vl-txlog-hdr-close {
  background:transparent; border:none;
  border-left:1px solid rgba(78,232,212,0.15);
  font-family:var(--vl-mono);
  font-size:10px; letter-spacing:0.22em;
  color:var(--vl-cyan); cursor:pointer;
  padding:0 16px !important; margin:0;
  align-self:stretch; display:flex; align-items:center;
  opacity:0.75; transition:opacity 0.12s ease, color 0.12s ease;
}
.vl-txlog-hdr-close:hover { opacity:1; color:var(--vl-cyan-b); }

/* Scrollable feed */
.vl-txlog-feed {
  flex:1; overflow-y:auto;
  padding:0 !important;
  scrollbar-width:thin;
  scrollbar-color:rgba(78,232,212,0.25) transparent;
}
.vl-txlog-feed::-webkit-scrollbar       { width:4px; }
.vl-txlog-feed::-webkit-scrollbar-track { background:transparent; }
.vl-txlog-feed::-webkit-scrollbar-thumb { background:rgba(78,232,212,0.22); border-radius:2px; }

/* Status line (loading / empty) */
.vl-txlog-status {
  font-size:11px; letter-spacing:0.2em;
  color:var(--vl-cyan-d); opacity:0.55;
  padding:28px 24px !important;
  animation:vl-pulse 2.5s ease-in-out infinite;
}

/* Day group — one per calendar date */
.vl-txlog-day {
  border-bottom:1px solid rgba(78,232,212,0.14);
}
.vl-txlog-day:last-child { border-bottom:none; }

/* Sticky day header */
.vl-txlog-day-hdr {
  position:sticky; top:0; z-index:1;
  display:flex; align-items:center; gap:10px;
  padding:9px 24px 8px !important;
  background:rgba(14,7,2,0.98);
  border-left:3px solid var(--vl-orange);
  border-bottom:1px solid rgba(232,133,53,0.2);
  box-shadow:inset 3px 0 18px rgba(232,133,53,0.04);
}
.vl-txlog-day-date {
  font-size:11px; letter-spacing:0.22em;
  color:var(--vl-green);
  text-shadow:0 0 6px rgba(41,232,122,0.4);
}
.vl-txlog-day-sep {
  font-size:9px; letter-spacing:0.1em;
  color:var(--vl-cyan-d); opacity:0.35;
}
.vl-txlog-day-count {
  font-size:9px; letter-spacing:0.2em;
  color:var(--vl-green); opacity:0.6;
}

/* Individual entry within a day */
.vl-txlog-entry {
  padding:13px 24px 15px !important;
  border-bottom:1px solid rgba(78,232,212,0.07);
  border-left:3px solid transparent;
  transition:background 0.1s ease, border-left-color 0.1s ease;
  cursor:default;
}
.vl-txlog-entry:last-child { border-bottom:none; }
.vl-txlog-entry:hover {
  background:rgba(78,232,212,0.03);
  border-left-color:rgba(78,232,212,0.35);
}

/* Meta row: sha + time + tod glyph */
.vl-txlog-meta {
  display:flex; align-items:center; gap:12px;
  padding-bottom:8px !important;
  margin-bottom:9px;
  border-bottom:1px solid rgba(78,232,212,0.08);
}
.vl-txlog-sha {
  font-size:9px; letter-spacing:0.18em;
  color:var(--vl-green);
  text-shadow:0 0 5px rgba(41,232,122,0.35);
  flex-shrink:0;
}
.vl-txlog-time {
  font-size:9px; letter-spacing:0.22em;
  color:var(--vl-green); opacity:0.75;
  flex-shrink:0;
}
.vl-txlog-tod {
  display:inline-flex; align-items:center; gap:5px;
  flex-shrink:0;
}
.vl-txlog-tod-glyph {
  font-size:9px;
  color:var(--vl-cyan); opacity:0.85;
  line-height:1;
}
.vl-txlog-tod-label {
  font-size:8px; letter-spacing:0.24em;
  color:var(--vl-cyan-d); opacity:0.72;
  text-transform:uppercase;
}

/* Transmission body */
.vl-txlog-body {
  font-size:12px; letter-spacing:0.08em; line-height:1.78;
  color:var(--vl-cyan);
  text-shadow:0 0 8px rgba(78,232,212,0.2);
  margin:0; padding:0;
}

/* Mobile overlay: full-width bottom sheet */
@media (max-width:705px) {
  .vl-txlog-overlay { padding:0; align-items:flex-end; }
  .vl-txlog-panel {
    width:100%; max-height:92vh;
    border-left:none; border-right:none; border-bottom:none;
    border-radius:0;
  }
  .vl-txlog-entry  { padding:13px 16px 12px !important; }
  .vl-txlog-body   { font-size:11px; }
  .vl-txlog-hdr    { padding:8px 12px 8px 0 !important; }
  .vl-txlog-hdr-path { padding:0 10px !important; }
  .vl-txlog-hdr-title { padding-left:12px !important; }
}

/* ── Boot overlay ── */
@keyframes vl-boot-exit {
  from { opacity:1; }
  to   { opacity:0; pointer-events:none; }
}
@keyframes vl-boot-line-in {
  from { opacity:0; transform:translateX(-6px); }
  to   { opacity:1; transform:translateX(0); }
}

#vl-boot-overlay {
  position:fixed; inset:0; z-index:999;
  background:#000;
  display:flex; align-items:center; justify-content:center;
  font-family:var(--vl-mono);
}
#vl-boot-overlay.vl-boot-exit {
  animation:vl-boot-exit 0.6s ease forwards;
}

#vl-boot-terminal {
  width:min(520px, 90vw);
  border:1px solid rgba(41,232,122,0.25);
  background:rgba(0,10,4,0.98);
  box-shadow:0 0 40px rgba(41,232,122,0.07), inset 0 0 60px rgba(0,0,0,0.6);
  padding:18px 22px 22px;
  display:flex; flex-direction:column; gap:14px;
}

#vl-boot-header {
  display:flex; justify-content:space-between; align-items:center;
  font-size:10px; letter-spacing:0.3em; text-transform:uppercase;
  color:var(--vl-green-d);
  border-bottom:1px solid rgba(41,232,122,0.12);
  padding-bottom:10px;
}

#vl-boot-blink {
  color:var(--vl-green);
  text-shadow:0 0 8px rgba(41,232,122,0.6);
  animation:vl-blink 1.4s ease-in-out infinite;
}

#vl-boot-lines {
  display:flex; flex-direction:column; gap:5px;
  min-height:160px;
}

.vl-boot-line {
  font-size:11px; letter-spacing:0.14em;
  color:var(--vl-green);
  text-shadow:0 0 6px rgba(41,232,122,0.25);
  line-height:1.6;
  animation:vl-boot-line-in 0.18s ease forwards;
}

.vl-boot-prompt {
  color:var(--vl-green-d);
}
`;
